import { useEffect } from 'react';
import { usePrevious } from 'react-use';

import { useAnnotatedPDFViewerContext } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.context';

import { useChatSideContentContext } from './context/chatSideContent.context';

/**
 * Opens the cited source document in the chat side content pane when a
 * CitationPopper in a chat message is clicked.
 *
 * The chat's outer AnnotatedPDFViewerProvider exists only so CitationPopper has
 * somewhere to resolve paragraph ids into annotations. This host watches that
 * context and, when a citation resolves, snapshots the result into the side
 * content descriptor. The pane renders it inside a fresh provider, so stepping
 * between passages or searching inside the pane can't disturb (or be disturbed
 * by) the chat transcript beside it.
 *
 * Mount inside the chat-level AnnotatedPDFViewerProvider, in place of the
 * citation modal host, wherever a ChatSideContentProvider is available.
 */
export function ChatCitationSideContentHost() {
  const {
    documentId,
    annotations,
    activeAnnotationIndex,
    setAnnotations,
    setActiveAnnotationIndex,
    setOverrideDocumentId,
  } = useAnnotatedPDFViewerContext();
  const { sideContent, openSideContent } = useChatSideContentContext();

  useEffect(() => {
    if (!documentId || !annotations?.length) {
      return;
    }

    openSideContent({
      type: 'citationDocument',
      documentId,
      annotations,
      activeAnnotationIndex,
    });
  }, [documentId, annotations, activeAnnotationIndex, openSideContent]);

  // When citation content stops being shown — closed, or replaced by another
  // content kind — clear the chat provider so re-clicking the same citation
  // re-resolves and re-opens the pane instead of being treated as an
  // already-open passage.
  const wasShowingCitation = usePrevious(
    sideContent?.type === 'citationDocument'
  );
  useEffect(() => {
    const isShowingCitation = sideContent?.type === 'citationDocument';
    if (wasShowingCitation && !isShowingCitation) {
      setAnnotations(undefined);
      setActiveAnnotationIndex(0);
      setOverrideDocumentId(null);
    }
  }, [
    sideContent,
    wasShowingCitation,
    setAnnotations,
    setActiveAnnotationIndex,
    setOverrideDocumentId,
  ]);

  return null;
}
